import type { ReactNode, ComponentType } from 'react';

import AutoDismissFlag from '@atlaskit/flag/auto-dismiss-flag';
import type { FlagProps } from '@atlaskit/flag/types';
import type { FlagOptions } from '@atlassian/forge-ui/custom-ui/bridge/types';

type FlagAction = NonNullable<FlagProps['actions']>[0];

type FlagType = 'success-circle' | 'success' | 'info' | 'error' | 'warning' | 'custom';

export interface FlagDescriptor {
	actions?: FlagAction[];
	appearance?: FlagProps['appearance'];
	close?: string;
	customComponent?: ComponentType<FlagProps>;
	customIcon?: JSX.Element;
	description?: string | ReactNode;
	id?: number | string;
	onClose?: () => void;
	title?: string | ReactNode;
	type?: FlagType;
}

export interface FlagFunctions {
	hideFlag: (id: number | string | undefined) => Promise<void>;
	showFlag: (flag: FlagDescriptor) => Promise<FlagDescriptor>;
}

export type FlagProviderHOC = ({
	children,
}: {
	children: (flags: FlagFunctions) => JSX.Element;
}) => JSX.Element;

export const getFlagProvider = ({
	showFlag,
	hideFlag,
}: FlagFunctions):
	| {
			closeFlag?: undefined;
			showFlag?: undefined;
	  }
	| {
			closeFlag: ({ id }: Pick<FlagOptions, 'id'>) => boolean;
			showFlag: (flagOptions: FlagOptions) => boolean;
	  } => {
	if (!showFlag || !hideFlag) {
		return {};
	}

	const createFlag = (flagOptions: FlagOptions) => {
		const { isAutoDismiss, ...restOptions } = flagOptions;
		const { type, appearance, actions } = flagOptions;
		const shouldAutoDismiss = isAutoDismiss ?? (appearance === 'info' || appearance === 'success');

		void showFlag({
			...restOptions,
			type: appearance ?? type,
			actions: actions?.map(({ text: content, onClick }) => ({
				onClick: () => onClick(),
				content,
			})),
			customComponent: shouldAutoDismiss ? AutoDismissFlag : undefined,
		});
		return true;
	};

	const closeFlag = ({ id }: Pick<FlagOptions, 'id'>) => {
		void hideFlag(id);
		return true;
	};

	return { showFlag: createFlag, closeFlag };
};
